// src/lib/robotsService.js
// Units: speed mm/s, accel mm/s^2, payload kg

import { collection, getDocs } from 'firebase/firestore'
import { db, isIOSCap } from '@/firebase/firebase'
import { setRobotsOverride } from './robots'

// ---------- helpers (unchanged behavior) ----------
function makeDerateFn(derating, maxPayload) {
  const knee   = derating?.knee ?? 0.25
  const vAtMax = derating?.speed_at_max ?? 0.6
  const aAtMax = derating?.accel_at_max ?? 0.5
  return function derate(payloadKg) {
    const mp = Number(maxPayload) || 1
    const p = Math.max(0, Number(payloadKg) || 0)
    const frac = Math.min(1, p / mp)
    if (frac <= knee) return [1, 1]
    const t = (frac - knee) / (1 - knee)
    const speedScale = 1 - (1 - vAtMax) * t
    const accelScale = 1 - (1 - aAtMax) * t
    return [speedScale, accelScale]
  }
}

const num = (v, d = null) => {
  const n = Number(v)
  return Number.isFinite(n) ? n : d
}

// kept for future use (not changing your current return shape)
function normalizeDoc(id, d) {
  const name = d.name || [d.vendor, d.model].filter(Boolean).join(' ')

  const reach =
    d.reach && typeof d.reach === 'object'
      ? {
          type: d.reach.type || 'cylindrical',
          radius: num(d.reach.radius, NaN),
          zMin: ('zMin' in d.reach) ? num(d.reach.zMin, -Infinity) : -Infinity,
          zMax: ('zMax' in d.reach) ? num(d.reach.zMax,  Infinity) :  Infinity,
        }
      : null

  const derating = {
    knee: num(d?.derating?.knee, 0.25),
    speed_at_max: num(d?.derating?.speed_at_max, 0.6),
    accel_at_max: num(d?.derating?.accel_at_max, 0.5),
  }

  const payloadMax = num(d.maxPayload ?? d.payloadMax ?? d.payload, NaN)

  return {
    id,
    name,
    vendor: d.vendor ?? null,
    model: d.model ?? null,
    axes: num(d.axes, 4),

    ratedSpeed: num(d.ratedSpeed ?? d.speedMax ?? d.speed, 700),
    ratedAccel: num(d.ratedAccel ?? d.accelMax ?? d.accel, 8000),
    minSpeed:   num(d.minSpeed, 50),
    minAccel:   num(d.minAccel, 500),

    maxPayload: payloadMax,
    payloadMax,

    reach,
    derating,
    derate: makeDerateFn(derating, payloadMax),
  }
}

// ---------- timeout wrapper (kept from your version) ----------
function withTimeout(promise, ms = 15000) {
  return Promise.race([
    promise,
    new Promise((_, reject) =>
      setTimeout(() => reject(new Error('Request timeout')), ms)
    )
  ])
}

// ---------- REST fallback (iOS only when SDK stalls) ----------
async function fetchRobotsREST(pageSize = 500) {
  const projectId = import.meta.env.VITE_FIREBASE_PROJECT_ID
  const url = `https://firestore.googleapis.com/v1/projects/${projectId}/databases/(default)/documents/robots?pageSize=${pageSize}`

  const res = await fetch(url)
  if (!res.ok) throw new Error(`REST robots failed: ${res.status}`)

  const json = await res.json()
  const docs = (json.documents || [])
  const out = {}
  for (const d of docs) {
    const id = d.name.split('/').pop()
    const obj = decodeFields(d.fields || {})
    out[id] = {
      ...obj,
      tier: (obj.tier === 'free' || obj.tier === 'standard') ? obj.tier : 'standard',
    }
  }
  return out
}

function decodeFields(fields) {
  const out = {}
  for (const [k, v] of Object.entries(fields)) out[k] = decodeValue(v)
  return out
}
function decodeValue(v) {
  if ('stringValue' in v) return v.stringValue
  if ('integerValue' in v) return Number(v.integerValue)
  if ('doubleValue' in v) return Number(v.doubleValue)
  if ('booleanValue' in v) return !!v.booleanValue
  if ('timestampValue' in v) return v.timestampValue
  if ('nullValue' in v) return null
  if ('arrayValue' in v) return (v.arrayValue.values || []).map(decodeValue)
  if ('mapValue' in v) return decodeFields(v.mapValue.fields || {})
  return v
}

// ---------- main loader (unchanged shape, adds iOS fallback) ----------
export async function loadRobotsFromFirestore() {
  // 🚀 iOS: go straight to REST (fast & reliable in WKWebView)
  if (isIOSCap) {
    console.log('[Robots] iOS → REST (fast path)')
    const out = await fetchRobotsREST()
    setRobotsOverride(out)
    return out
  }

  // Web/Android: SDK path unchanged
  try {
    console.log('[Robots] SDK fetch…')
    const snap = await getDocs(collection(db, 'robots'))

    const out = {}
    snap.forEach(docSnap => {
      const d = docSnap.data() || {}
      out[docSnap.id] = {
        ...d,
        tier: (d.tier === 'free' || d.tier === 'standard') ? d.tier : 'standard',
      }
    })
    setRobotsOverride(out)
    return out
  } catch (error) {
    console.error('[Robots] SDK fetch failed:', {
      code: error?.code,
      message: error?.message,
      name: error?.name
    })
    throw error
  }
}

